import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Palette, Upload, Trash2, ImageIcon, Save, Sparkles } from "lucide-react";

/**
 * Super-admin-only white-labeling panel. Configures the PLATFORM's brand name,
 * tagline, primary hex color and logo. Used on LoginPage + sidebar fallback + emails.
 * Rendered inside SettingsPage when user.role === "super_admin".
 */
export default function PlatformBrandingSection() {
  const { platform, refresh } = useAuth();
  const [cfg, setCfg] = useState({ brand_name: "", tagline: "", primary_color: "#BE1E1E", support_email: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (platform) {
      setCfg({
        brand_name: platform.brand_name || "",
        tagline: platform.tagline || "",
        primary_color: platform.primary_color || "#BE1E1E",
        support_email: platform.support_email || "",
      });
    }
  }, [platform]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/platform/config", cfg);
      toast.success("Platform branding saved");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/platform/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Platform logo updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove platform logo? Fallback initial letter will show instead.")) return;
    try { await api.delete("/platform/logo"); toast.success("Platform logo removed"); await refresh(); }
    catch { toast.error("Failed"); }
  };

  return (
    <section className="border border-primary/40 rounded-md bg-red-50/40 p-6 card-elev" data-testid="platform-branding-section">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="overline flex items-center gap-2 text-primary"><Sparkles size={12} /> Platform · Super admin only</div>
          <h2 className="font-display text-xl mt-1">White-label platform branding</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Applied on the sign-in page, sidebar fallback (when tenants have no logo) and outbound email footers.
            Tenants can still override with their own logo on <span className="mono">Settings → Company logo</span>.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="h-24 min-w-[6rem] max-w-[16rem] px-3 rounded-md border border-border bg-white flex items-center justify-center overflow-hidden" data-testid="platform-logo-preview">
            {platform?.logo_url ? (
              <img src={fileUrl(platform.logo_url)} alt="Platform logo" className="block h-full w-auto max-w-full object-contain" />
            ) : (
              <ImageIcon size={28} className="text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" hidden data-testid="platform-logo-file"
              onChange={(e) => upload(e.target.files?.[0])} />
            <Button data-testid="platform-logo-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
              <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Upload logo"}
            </Button>
            {platform?.logo_url && (
              <Button data-testid="platform-logo-remove" onClick={removeLogo} variant="outline" className="rounded-md">
                <Trash2 size={14} className="mr-2" /> Remove
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="overline">Brand name</span>
          <Input data-testid="platform-brand-name" value={cfg.brand_name}
            placeholder="e.g. Mailroom.OS · Mailit · CourierHub"
            onChange={(e) => setCfg({ ...cfg, brand_name: e.target.value })}
            className="mt-1 rounded-md border-input focus-visible:ring-primary" />
        </label>
        <label className="block">
          <span className="overline">Tagline</span>
          <Input data-testid="platform-tagline" value={cfg.tagline}
            placeholder="e.g. Enterprise mailroom platform"
            onChange={(e) => setCfg({ ...cfg, tagline: e.target.value })}
            className="mt-1 rounded-md border-input focus-visible:ring-primary" />
        </label>
        <label className="block">
          <span className="overline flex items-center gap-1.5"><Palette size={11} /> Primary color (HEX)</span>
          <div className="mt-1 flex items-center gap-2">
            <Input data-testid="platform-primary-color" value={cfg.primary_color}
              placeholder="#BE1E1E"
              onChange={(e) => setCfg({ ...cfg, primary_color: e.target.value })}
              className="rounded-md border-input focus-visible:ring-primary font-mono" />
            <div className="w-10 h-10 rounded-md border border-border shrink-0"
                 style={{ backgroundColor: cfg.primary_color || "#BE1E1E" }} />
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            Applied via <span className="mono">--primary-hex</span> CSS variable. Restart / hard-refresh to see full effect.
          </div>
        </label>
        <label className="block">
          <span className="overline">Support email (footer)</span>
          <Input data-testid="platform-support-email" value={cfg.support_email} type="email"
            placeholder="support@yourdomain.com"
            onChange={(e) => setCfg({ ...cfg, support_email: e.target.value })}
            className="mt-1 rounded-md border-input focus-visible:ring-primary" />
        </label>
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="platform-branding-save" onClick={save} disabled={saving}
          className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save platform branding"}
        </Button>
      </div>
    </section>
  );
}
